/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/peppolInvoices.scss';
import {
    AwesomeIcon,
    ChangeFunction,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem,
    SelectItemShape
} from '@dvrd/dvr-controls';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

import {Icon} from '@iconify/react';

import classNames from 'classnames';

import React, {KeyboardEventHandler, MouseEventHandler, ReactNode, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../../models/invoiceModel';

import {userHasRight} from '../../../util/accessUtil';

import {getCountryCode} from '../../../util/countryUtil';

import {getInvoiceOptions} from '../../../util/dashboardUtil';

import {InvoiceOptionName, PeppolState} from '../../../util/enums';

import {InvoiceOption, PeppolStatus} from '../../../util/types';

import {formatSimpleDate, onCopy, parseMoney} from '../../../util/utils';

import {BolmateInput} from '../../controls';

import {AppContext} from '../../misc/appContext';

import CopyIcon from '../../misc/copyIcon';

import CountryFlag from '../../misc/countryFlag';

import BolmateSwitch from '../../controls/switch/bolmateSwitch';

import CommonPagination from '../../misc/commonPagination';

import checkAll from '@iconify/icons-bi/check-all';

import pauseCircle from '@iconify/icons-feather/pause-circle';

import IDate from '@dvrd/idate';

type InvoiceClicker = (invoice: Invoice) => MouseEventHandler;

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onChangePeppolState: (peppolState: PeppolState | null) => MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onToggleEnabled: ChangeFunction<boolean>;

    // Invoice options
    onClickDetails: InvoiceClicker;
    onClickPreview: InvoiceClicker;
    onClickSend: InvoiceClicker;
    onCopyInvoiceID: InvoiceClicker;

    counts: Record<PeppolState, number>;
    lastCounts: Record<PeppolState, number>;
    invoices: Array<Invoice>;
    peppolState: PeppolState | null;
    total: number;
    page: number;
    limit: number;
    search: string;
    loading: boolean;
    loadingCounts: boolean;
    peppolStatus: PeppolStatus;
}

const costPrice = 0.2;
const limitItems: Array<SelectItemShape> = [
    {label: 10, value: 10},
    {label: 20, value: 20},
    {label: 50, value: 50}
];

export default function PeppolInvoices(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const {
        onChangeSearch,
        onEnterSearch,
        onChangePeppolState,
        onChangePage,
        onChangeLimit,
        counts,
        invoices,
        peppolState,
        total,
        page,
        limit,
        search,
        loading,
        loadingCounts,
        lastCounts,
        onClickDetails,
        onClickPreview,
        onClickSend,
        onCopyInvoiceID,
        onToggleEnabled,
        peppolStatus
    } = props;

    function getInvoiceOptionsMenu(invoice: Invoice) {
        const invoiceOptions = getInvoiceOptions(invoice, user).filter((option: InvoiceOption) =>
            userHasRight(context, option.accessKey, invoice.accountId)
        );
        const options: OptionsMenuItem[] = [];
        for (const option of invoiceOptions) {
            let onClick: MouseEventHandler | undefined = undefined;
            const optionType = option.type;
            if (optionType === InvoiceOptionName.DETAILS) onClick = onClickDetails(invoice);
            else if (optionType === InvoiceOptionName.PREVIEW) onClick = onClickPreview(invoice);
            else if (optionType === InvoiceOptionName.SEND_PEPPOL) onClick = onClickSend(invoice);
            else if (optionType === InvoiceOptionName.COPY_ID) onClick = onCopyInvoiceID(invoice);
            if (onClick)
                options.push({
                    onClick,
                    icon: option.icon,
                    label: option.label,
                    disabled: option.disabled
                });
        }
        return options;
    }

    function renderTopBar() {
        return (
            <div className='top-bar'>
                {renderStat('paper-plane', PeppolState.SENT)}
                {renderStat('exclamation-circle', PeppolState.FAILED)}
                {renderCosts()}
                {renderSettings()}
            </div>
        );
    }

    function renderStat(icon: IconName, peppolState: PeppolState.SENT | PeppolState.FAILED) {
        const label =
            peppolState === PeppolState.SENT
                ? t('invoice.peppol.stat.delivered', 'Peppol e-facturen afgeleverd')
                : t('invoice.peppol.stat.failed', 'Peppol e-facturen mislukt');
        const count = loadingCounts ? '-' : (counts[peppolState] ?? 0);
        const lastCount = loadingCounts ? '-' : (lastCounts[peppolState] ?? 0);
        return (
            <div className='peppol-stat' onClick={onChangePeppolState(peppolState)}>
                <Loader active={loadingCounts} size='2.5rem' />
                <div className={classNames('icon-container', peppolState.toLowerCase())}>
                    <AwesomeIcon name={icon} className='stat-icon' />
                </div>
                <div className='stat-content'>
                    <label className='stat-label'>{label.toUpperCase()}</label>
                    <label className='stat-count'>{count}</label>
                    <label className='stat-last-count'>
                        <span className='count'>+{lastCount}</span>{' '}
                        {t('invoice.peppol.last30Days', 'AFGELOPEN 30 DAGEN')}
                    </label>
                </div>
            </div>
        );
    }

    function renderCosts() {
        const costs = (counts[PeppolState.SENT] ?? 0) * costPrice;
        return (
            <div className='peppol-stat no-click'>
                <Loader active={loadingCounts} size='2.5rem' />
                <div className='icon-container costs'>
                    <AwesomeIcon name='euro-sign' className='stat-icon' />
                </div>
                <div className='stat-content'>
                    <label className='stat-label'>{t('invoice.peppol.totalCosts', 'TOTALE KOSTEN')}</label>
                    <label className='stat-count'>{parseMoney(costs)}</label>
                    <label className='stat-last-count'>
                        {t('invoice.peppol.costsPer', 'KOSTEN €0,20 PER E-FACTUUR')}
                    </label>
                </div>
            </div>
        );
    }

    function renderSettings() {
        return (
            <div className='peppol-stat no-click settings'>
                <Loader active={loadingCounts} />
                <div className='icon-container'>
                    <AwesomeIcon name='info-circle' className='stat-icon' />
                    {renderPeppolInfo()}
                </div>
                <div className='stat-content'>
                    <div className='peppol-switch'>
                        <label className='peppol-switch-label'>
                            {t('invoice.peppol.usePeppol', 'Peppol gebruiken')}
                        </label>
                        <BolmateSwitch
                            value={peppolStatus.enabled}
                            onChange={onToggleEnabled}
                            className='common-switch'
                            activeGreen
                        />
                    </div>
                    {renderPeppolStatus()}
                </div>
            </div>
        );
    }

    function renderPeppolInfo() {
        const peppolMandatory = IDate.now().isAtOrAfter(new IDate('2026-01-01'), 'day');
        const vatNumber = account?.getVatNumber('BE');
        const peppolID = !vatNumber || !peppolStatus.hasRegistration ? null : `0208:${vatNumber.substring(2)}`;
        return (
            <p className='info-text'>
                <b>{t('invoice.peppol.info.title', 'E-facturen verzenden via Peppol')}</b>
                <br />
                {peppolMandatory
                    ? t('invoice.peppol.info.since', 'Sinds')
                    : t('invoice.peppol.info.from', 'Vanaf')}{' '}
                {t(
                    'invoice.peppol.info.mandatoryDate',
                    '1 januari 2026 is het verplicht om als Belgische verkoper de factuur voor zakelijke Belgische orders via het Peppol netwerk te verzenden.'
                )}
                <br />
                {t(
                    'invoice.peppol.info.integration',
                    'Je kunt hiervoor gebruik maken van onze Peppol-integratie, waarmee je volledig automatisch jouw zakelijke e-facturen via het Peppol netwerk kunt verzenden.'
                )}
                <br />
                <br />
                <b>{t('invoice.peppol.info.registrationTitle', 'Peppol registratie')}</b>
                <br />
                {!peppolID ? (
                    <>
                        {t(
                            'invoice.peppol.info.needRegistration',
                            'Het enige wat je hier zelf voor hoeft te regelen is de Peppol registratie van jouw BTW/KBO-nummer. Je kunt dit doen bij een Peppol provider.'
                        )}
                        <br />
                        <br />
                        {t(
                            'invoice.peppol.info.afterRegistration',
                            'Zodra jouw Peppol registratie gelukt is, kun je de Peppol-integratie inschakelen. Wij halen automatisch jouw geregistreerde Peppol ID op en gebruiken deze om jouw e-facturen te verzenden.'
                        )}
                    </>
                ) : (
                    <>
                        {t(
                            'invoice.peppol.info.haveRegistrationBefore',
                            'Je hebt al een Peppol registratie voor jouw BTW-nummer. We gebruiken jouw Peppol ID ('
                        )}
                        <i>{peppolID}</i>
                        {t(
                            'invoice.peppol.info.haveRegistrationAfter',
                            ') voor het versturen van de e-facturen via het Peppol netwerk.'
                        )}
                    </>
                )}
                <br />
                <br />
                <b>{t('invoice.peppol.info.costsTitle', 'Kosten')}</b>
                <br />
                {t(
                    'invoice.peppol.info.costsBody',
                    'Het verzenden van e-facturen via het Peppol netwerk kost €0,20 per e-factuur. Alleen facturen voor Belgische zakelijke klanten (met BTW-nummer) worden via Peppol verzonden, dit is gemiddeld 0-1% van de orders.'
                )}
            </p>
        );
    }

    function renderPeppolStatus() {
        let statuses: Array<[ReactNode, string]>;
        if (peppolStatus.enabled) {
            statuses = [
                [
                    <Icon icon={checkAll} className='status-icon check' />,
                    t('invoice.peppol.status.linked', 'Gekoppeld aan Peppol')
                ],
                [
                    <Icon icon={checkAll} className='status-icon check' />,
                    t('invoice.peppol.status.sending', 'Peppol B2B e-facturen worden verzonden')
                ]
            ];
        } else {
            if (peppolStatus.hasRegistration) {
                statuses = [
                    [
                        <Icon icon={checkAll} className='status-icon check' />,
                        t('invoice.peppol.status.linked', 'Gekoppeld aan Peppol')
                    ],
                    [
                        <Icon icon={pauseCircle} className='status-icon' />,
                        t('invoice.peppol.status.notSending', 'Peppol B2B e-facturen worden niet verzonden')
                    ]
                ];
            } else {
                statuses = [
                    [
                        <Icon icon={pauseCircle} className='status-icon' />,
                        t('invoice.peppol.status.notLinked', 'Niet gekoppeld aan Peppol')
                    ],
                    [
                        <Icon icon={pauseCircle} className='status-icon' />,
                        t('invoice.peppol.status.notSending', 'Peppol B2B e-facturen worden niet verzonden')
                    ]
                ];
            }
        }
        return statuses.map(([icon, label], index) => (
            <label key={index} className='peppol-status'>
                {icon} {label}
            </label>
        ));
    }

    function renderInvoicesContainer() {
        return (
            <div className='peppol-invoices-container'>
                <Loader active={loading} />
                <label className='title'>
                    {t('invoice.peppol.listTitle', 'Peppol e-facturen (voor Belgische zakelijke klanten)')}
                </label>
                <BolmateInput
                    onChange={onChangeSearch}
                    value={search}
                    onEnter={onEnterSearch}
                    label={t('invoice.peppol.searchLabel', 'Factuur zoeken')}
                    placeholder={t('invoice.peppol.searchPlaceholder', 'Zoek op ordernummer')}
                    autoFocus
                />
                {renderPeppolStates()}
                {renderInvoices()}
                <CommonPagination
                    onChangePage={onChangePage}
                    onChangeLimit={onChangeLimit}
                    page={page}
                    total={total}
                    limit={limit}
                    limitItems={limitItems}
                />
            </div>
        );
    }

    function renderPeppolStates() {
        return (
            <div className='peppol-states'>
                {renderPeppolState(null)}
                {renderPeppolState(PeppolState.TRANSIT)}
                {renderPeppolState(PeppolState.SENT)}
                {renderPeppolState(PeppolState.FAILED)}
            </div>
        );
    }

    function renderPeppolState(_peppolState: PeppolState | null) {
        let label: string;
        let count: number;
        if (!_peppolState) {
            label = t('invoice.peppol.state.all', 'Alle');
            count = Object.values(counts).reduce((acc, cur) => acc + cur, 0);
        } else {
            label = {
                [PeppolState.TRANSIT]: t('invoice.peppol.state.transit', 'Verzonden'),
                [PeppolState.SENT]: t('invoice.peppol.state.sent', 'Afgeleverd'),
                [PeppolState.FAILED]: t('invoice.peppol.state.failed', 'Verzending mislukt')
            }[_peppolState];
            count = counts[_peppolState] ?? 0;
        }
        const active = _peppolState === peppolState;
        return (
            <div className={classNames('peppol-state', active && 'active')} onClick={onChangePeppolState(_peppolState)}>
                <label className='peppol-state-label'>
                    {label} ({count})
                </label>
            </div>
        );
    }

    function renderInvoices() {
        return (
            <div className='peppol-invoices'>
                <div className='row head'>
                    <label>{t('invoice.peppol.col.orderNumber', 'ORDERNUMMER')}</label>
                    <label>{t('invoice.peppol.col.invoiceNumber', 'FACTUURNUMMER')}</label>
                    <label>{t('invoice.peppol.col.name', 'NAAM')}</label>
                    <label>{t('invoice.peppol.col.date', 'DATUM')}</label>
                    <label>{t('invoice.peppol.col.amount', 'BEDRAG')}</label>
                    <label>{t('invoice.peppol.col.status', 'STATUS')}</label>
                    <label className='invoice-actions'>{t('invoice.peppol.col.actions', 'ACTIES')}</label>
                </div>
                {invoices.map(renderInvoice)}
            </div>
        );
    }

    function renderInvoice(invoice: Invoice) {
        let optionsMenu: OptionsMenu;

        function onClickRow() {
            optionsMenu.onOpenExternal();
        }

        const invoiceOptions = getInvoiceOptionsMenu(invoice);
        return (
            <div key={invoice.id} className='row' onClick={onClickRow}>
                <div className='with-copy-action'>
                    <label className='bold ref'>{invoice.orderId}</label>
                    <CopyIcon onClick={onCopy(invoice.orderId, true)} className='copy-action' autoReset />
                </div>
                <div className='with-copy-action'>
                    <label>{invoice.invoiceNum}</label>
                    <CopyIcon onClick={onCopy(invoice.invoiceNum, true)} className='copy-action' autoReset />
                </div>
                <div className='with-copy-action'>
                    <label>
                        <CountryFlag countryCode={getCountryCode(invoice.country)} /> {invoice.fullName}
                    </label>
                    <CopyIcon onClick={onCopy(invoice.fullName, true)} className='copy-action' autoReset />
                </div>
                <label>{formatSimpleDate(invoice.momentDate)}</label>
                <label>&euro; {parseMoney(invoice.total)}</label>
                {renderInvoiceStatus(invoice)}
                <OptionsMenu
                    options={invoiceOptions}
                    placement={MenuPlacement.BOTTOM_LEFT}
                    optionsComponent={<Icon icon={optionsHorizontal} className='options-icon' />}
                    containerClass='common-options'
                    ref={(ref: OptionsMenu) => {
                        optionsMenu = ref;
                    }}
                />
            </div>
        );
    }

    function renderInvoiceStatus(invoice: Invoice) {
        const {peppolState} = invoice;
        let label: string;
        let info: string | null = null;
        if (peppolState === PeppolState.SENT) label = t('invoice.peppol.state.sent', 'Afgeleverd');
        else if (peppolState === PeppolState.TRANSIT) {
            label = t('invoice.peppol.state.transit', 'Verzonden');
            info = t('invoice.peppol.state.transitInfo', 'De e-factuur is onderweg naar de klant');
        } else if (peppolState === null) {
            label = t('invoice.peppol.state.notSent', 'Nog niet verzonden');
            info = t('invoice.peppol.state.notSentInfo', 'De e-factuur is nog niet verzonden naar de klant');
        } else {
            label = t('invoice.peppol.state.error', 'Fout bij verzending');
            info = invoice.peppolError;
        }
        return (
            <div className={classNames('peppol-status', peppolState?.toLowerCase())}>
                <label className='peppol-status-label'>{label}</label>
                {!!info && (
                    <div className='peppol-error-info'>
                        <AwesomeIcon name='info-circle' className='info-icon' />
                        <p className='error-info'>{info}</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='peppol-invoices-page'>
            {renderTopBar()}
            {renderInvoicesContainer()}
        </div>
    );
}
